import { useCallback, useEffect, useRef, useState } from "react";

import toast from "react-hot-toast";
import { useHistory } from "react-router-dom";

import { useAuthenticator, useEnrollment } from "@ip-hooks/mfa";
import { publicPath, trackEvent } from "@ip-utils";

import {
  NoBorderButtonWithPlusIcon,
  PrimaryButton,
  WhiteButton,
} from "../common/button";
import { SimpleAlertWithGrayFooter } from "../common/modal";
import { HorizontalSplit, TitleWithDescription } from "../common/stack";
import AuthenticatorTypeSelector from "./AuthenticatorTypeSelector";

export default function AuthenticatorList() {
  const history = useHistory();
  const [loadingIndicator, setLoadingIndicator] = useState(false);
  const [shouldDisplayAuthenticators, setShouldDisplayAuthenticators] =
    useState(false);
  const [selectAuthId, setSelectAuthId] = useState("");
  const [open, setOpen] = useState(false);

  const [toggleAddAuthenticator, setToggleAddAuthenticator] = useState(false);
  const [selectedAuthenticatorType, setSelectedAuthenticatorType] =
    useState("otp");

  const { enrollWithOTP: enrollOTP, enrollWithGuardian } = useEnrollment();
  const [authenticators, setAuthenticators] = useState<any[]>([]);
  const {
    getAuthenticators,
    deleteAuthenticator,
    deleteInActiveAuthenticators,
  } = useAuthenticator();

  const mountedRef: any = useRef(true);

  const deleteInActiveAuthenticatorsCallback = useCallback(async () => {
    return await deleteInActiveAuthenticators();
  }, [deleteInActiveAuthenticators]);

  const getAuthenticatorsCallback = useCallback(async () => {
    return await getAuthenticators();
  }, [getAuthenticators]);

  const loadAuthenticator = useCallback(async () => {
    if (mountedRef.current) {
      const authenticators = await getAuthenticatorsCallback();
      setAuthenticators(authenticators);

      let isEnrolling =
        localStorage.getItem("isEnrolling") ?? '{"isEnrolling": false}';
      isEnrolling = JSON.parse(isEnrolling);

      if (isEnrolling) {
        setShouldDisplayAuthenticators(true);
      } else {
        const { status, errors } = await deleteInActiveAuthenticatorsCallback();

        if (status === 204) {
          setShouldDisplayAuthenticators(true);
        } else if (errors && errors?.length !== 0) {
          const retryDeletingAuthenticators = async () => {
            const { status } = await deleteInActiveAuthenticatorsCallback();
            if (status === 204) {
              const updatedAuthenticators = await getAuthenticatorsCallback();
              setAuthenticators(updatedAuthenticators);
              setShouldDisplayAuthenticators(true);
            } else {
              toast.error(errors[0].error_description);
            }
          };
          setTimeout(async () => {
            await retryDeletingAuthenticators();
          }, 2000);
        }
      }
    }
  }, [getAuthenticatorsCallback, deleteInActiveAuthenticatorsCallback]);

  useEffect(() => {
    loadAuthenticator();

    return () => {
      mountedRef.current = false;
    };
  }, [loadAuthenticator]);

  const enrollWithOTP = async () => {
    trackEvent("MFA", "Start enrollment with OTP");

    toast.loading("Loading");
    const res = await enrollOTP();
    const { barcode_uri } = await res.json();
    toast.dismiss();

    toast.loading("Redirecting");

    localStorage.setItem("isEnrolling", "true");

    setTimeout(() => {
      history.push(`/mfa-otp-enrollment?barcode_uri=${barcode_uri}`);
      toast.dismiss();
    }, 2000);
  };

  const enrollWithSMS = async () => {
    trackEvent("MFA", "Start enrollment with SMS");

    localStorage.setItem("isEnrolling", "true");

    history.push(`/mfa-sms-enrollment`);
  };

  const enrollWithVoice = async () => {
    trackEvent("MFA", "Start enrollment with VOICE");

    localStorage.setItem("isEnrolling", "true");

    history.push(`/mfa-voice-enrollment`);
  };

  const enrollWithPush = async () => {
    trackEvent("MFA", "Start enrollment with PUSH");

    toast.loading("Loading");
    const res = await enrollWithGuardian();
    const { barcode_uri, oob_code } = await res.json();
    toast.dismiss();

    toast.loading("Redirecting");

    localStorage.setItem("isEnrolling", "true");

    setTimeout(() => {
      history.push(
        `/mfa-push-enrollment?barcode_uri=${barcode_uri}&oob_code=${oob_code}`
      );
      toast.dismiss();
    }, 2000);
  };

  const handleOpenDeleteAuthModal = (id: string) => {
    setSelectAuthId(id);
    setOpen(true);
  };

  const handleOnDeleteAuth = async () => {
    trackEvent("MFA", "Delete authenticator");

    setLoadingIndicator(true);
    toast.loading("Deleting");

    const res = await deleteAuthenticator(selectAuthId);

    if (res.status === 204) {
      trackEvent("MFA", "Delete authenticator successful");
      const res = await getAuthenticators();
      setAuthenticators(res);
      setLoadingIndicator(false);
      toast.dismiss();

      toast("Done");
      setOpen(false);
    } else {
      const { error_description } = await res.json();
      toast.error(error_description);
    }
  };

  const renderDevices = () => {
    const renderType = (key: string) => {
      switch (key) {
        case "otp":
          return "One-time Password";
        case "oob":
          return "Out-of-Band";
        default:
          return "Recovery code";
      }
    };

    const renderChannel = (key: string) => {
      switch (key) {
        case "auth0":
          return "Push";
        case "sms":
          return "SMS";
        case "voice":
          return "Call";
        default:
          return "";
      }
    };

    return authenticators.map((authenticator: any, i: number) => {
      const {
        id,
        authenticator_type: type,
        oob_channel: channel,
        name,
      } = authenticator;
      return (
        <div key={i} className="pb-4 mt-8 border-b border-gray-300">
          <HorizontalSplit
            left={
              <div className="flex items-center">
                <img
                  className="mr-2"
                  alt="totp"
                  src={publicPath("/media/totp.svg")}
                />
                {name ? name + " - " : ""}
                {renderType(type)}
                {channel ? " " + renderChannel(channel) : ""}
              </div>
            }
            right={
              <div>
                <WhiteButton
                  text="Remove"
                  onClick={() => handleOpenDeleteAuthModal(id)}
                />
              </div>
            }
          />
        </div>
      );
    });
  };

  const handleOnSelectAuthenticatorType = (option: any) => {
    const { name } = option;
    switch (name) {
      case "OTP":
        setSelectedAuthenticatorType("otp");
        break;

      case "SMS":
        setSelectedAuthenticatorType("sms");
        break;

      case "VOICE":
        setSelectedAuthenticatorType("voice");
        break;

      case "PUSH":
        setSelectedAuthenticatorType("push");
        break;

      default:
        break;
    }
  };

  const handleContinueWithSelectedAuthenticatorTypeOnClick = async () => {
    switch (selectedAuthenticatorType) {
      case "otp":
        await enrollWithOTP();
        break;

      case "sms":
        await enrollWithSMS();
        break;

      case "voice":
        await enrollWithVoice();
        break;

      case "push":
        await enrollWithPush();
        break;

      default:
        break;
    }
  };

  if (!shouldDisplayAuthenticators) {
    return <div>Loading</div>;
  }

  return (
    <div className="flex items-center mb-32">
      <div className="w-full">
        <TitleWithDescription
          title="Multi-Factor Authentication"
          description="Currently enrolled authenticators"
        />

        <div className="mt-8">{renderDevices()}</div>

        <div className="flex mt-4">
          {toggleAddAuthenticator ? (
            <div>
              <AuthenticatorTypeSelector
                onSelect={handleOnSelectAuthenticatorType}
              />
              <div className="mt-4">
                <PrimaryButton
                  text="continue"
                  onClick={handleContinueWithSelectedAuthenticatorTypeOnClick}
                  size="lg"
                />
              </div>
            </div>
          ) : (
            <NoBorderButtonWithPlusIcon
              text="Add authenticator"
              onClick={() => setToggleAddAuthenticator(true)}
            />
          )}
        </div>
      </div>

      <SimpleAlertWithGrayFooter
        open={open}
        onClose={() => setOpen(false)}
        mainButtonOnClick={handleOnDeleteAuth}
        title="Remove authenticator"
        text="This will permanently remove this authenticator."
        mainButtonText={"Confirm"}
        loading={loadingIndicator}
      />
    </div>
  );
}
